import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, ArrowRight, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const transactionId = searchParams.get("transaction_id");
    const txRef = searchParams.get("tx_ref");
    const status = searchParams.get("status");
    const reference = searchParams.get("reference") || searchParams.get("trxref");

    // Check for explicit cancellation first (Flutterwave sends ?status=cancelled)
    if (status === "cancelled") {
      setState("failed");
      setError("Payment was cancelled.");
      return;
    }

    if (transactionId) {
      verifyFlutterwave(transactionId, txRef);
    } else if (reference) {
      verifyPaystack(reference);
    } else {
      setState("failed");
      setError("No payment reference found.");
    }
  }, []);

  const verifyPaystack = async (reference) => {
    try {
      const { data } = await API.post("/payments/paystack/verify", { reference });
      if (data.success) {
        setResult(data);
        setState("success");
      } else {
        setState("failed");
        setError("Payment could not be verified.");
      }
    } catch (err) {
      setState("failed");
      setError(err?.response?.data?.detail || "Payment verification failed.");
    }
  };

  const verifyFlutterwave = async (transactionId, txRef) => {
    try {
      const { data } = await API.post("/payments/flutterwave/verify", {
        transaction_id: transactionId,
        tx_ref: txRef,
      });
      if (data.success) {
        setResult(data);
        setState("success");
      } else {
        setState("failed");
        setError("Payment could not be verified.");
      }
    } catch (err) {
      setState("failed");
      setError(err?.response?.data?.detail || "Payment verification failed.");
    }
  };

  const handleContinue = () => {
    if (result?.type === "subscription") {
      navigate("/dashboard/sender-ids");
    } else {
      navigate("/dashboard/wallet");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded-lg bg-[#0c1e41] flex items-center justify-center shadow-sm">
            <MessageSquare size={15} className="text-white" />
          </div>
          <span className="text-lg font-extrabold text-slate-900">Sendora</span>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm text-center">
          {state === "verifying" && (
            <>
              <div className="w-16 h-16 bg-[#eef1f8] rounded-full flex items-center justify-center mx-auto mb-5">
                <Loader2 size={28} className="text-[#0c1e41] animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-testid="callback-verifying">Verifying Payment</h2>
              <p className="text-sm text-slate-500">Please wait while we confirm your payment...</p>
            </>
          )}

          {state === "success" && result && (
            <>
              <div className="w-16 h-16 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-testid="callback-success">Payment Successful!</h2>
              <p className="text-sm text-slate-500 mb-1">Your payment has been confirmed.</p>

              {result.type === "subscription" ? (
                <div className="my-5 bg-emerald-50 border border-emerald-200 rounded-xl py-4 px-6">
                  <div className="text-2xl font-extrabold text-emerald-700" data-testid="callback-plan-name">{result.plan_name}</div>
                  <div className="text-sm text-emerald-600 mt-1 capitalize">
                    {result.billing_cycle} subscription activated
                  </div>
                  {result.credits > 0 && (
                    <div className="text-xs text-emerald-500 mt-1">
                      +{result.credits.toLocaleString()} free credits added to your wallet
                    </div>
                  )}
                </div>
              ) : (
                <div className="my-5 bg-emerald-50 border border-emerald-200 rounded-xl py-4 px-6">
                  <div className="text-3xl font-extrabold font-mono text-emerald-700" data-testid="callback-credits">
                    {result.credits?.toLocaleString()}
                  </div>
                  <div className="text-sm text-emerald-600 mt-1">SMS credits added to your wallet</div>
                </div>
              )}

              <Button
                className="w-full bg-[#0c1e41] hover:bg-[#091629] text-white shadow-sm"
                onClick={handleContinue}
                data-testid="callback-continue-btn"
              >
                {result.type === "subscription" ? "View Sender IDs" : "Go to Wallet"}
                <ArrowRight size={15} className="ml-2" />
              </Button>
              <Button
                variant="ghost"
                className="w-full mt-2 text-slate-500 hover:text-[#0c1e41]"
                onClick={() => navigate("/dashboard")}
              >
                Back to Dashboard
              </Button>
            </>
          )}

          {state === "failed" && (
            <>
              <div className="w-16 h-16 bg-red-50 border border-red-200 rounded-full flex items-center justify-center mx-auto mb-5">
                <XCircle size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-testid="callback-failed">Payment Not Confirmed</h2>
              <p className="text-sm text-slate-500 mb-4" data-testid="callback-error-msg">
                {error || "We could not verify your payment. If you were charged, contact support."}
              </p>
              <Button
                className="w-full bg-[#0c1e41] hover:bg-[#091629] text-white shadow-sm"
                onClick={() => navigate("/dashboard/wallet/buy")}
                data-testid="callback-retry-btn"
              >
                Try Again
              </Button>
              <Button
                variant="ghost"
                className="w-full mt-2 text-slate-500"
                onClick={() => navigate("/dashboard")}
              >
                Back to Dashboard
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
